import { ConnectError } from '@connectrpc/connect';
import type { RevitOperationProgressSnapshot } from './operationProgressClient';
import { pollRevitOperation } from './operationProgressClient';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { adaptPcfParameterCatalog, parsePcfParameterCatalogResultJson } from './pcfParameterCatalog';
import type { PcfParameterCatalogResult } from './pcfParameterCatalog';
import { getPcfMappingGrpcClient } from './pcfMappingGrpcClient';

type CatalogOptions = {
  readonly onProgress?: (progress: RevitOperationProgressSnapshot) => void;
};

const unavailableResult = (): PcfParameterCatalogResult => ({
  success: false,
  parameterNames: [],
  parameterCandidates: [],
  error: 'gRPC transport is unavailable',
});

export async function getAllParameterNamesGrpc(
  options?: CatalogOptions,
): Promise<PcfParameterCatalogResult> {
  const client = getPcfMappingGrpcClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(
      'GetAllParameterNames: gRPC transport is unavailable',
    );
    return unavailableResult();
  }

  try {
    if (options?.onProgress) {
      try {
        const startResponse = await client.startGetAllParameterNames({});
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parsePcfParameterCatalogResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'GetAllParameterNames operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetAllParameterNames: ${error}`);
            return { success: false, parameterNames: [], parameterCandidates: [], error };
          }
          return operationResult.result;
        }
      } catch (error) {
        if (!(error instanceof ConnectError)) {
          throw error;
        }
      }
    }

    const result = adaptPcfParameterCatalog(await client.getAllParameterNames({}));
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(
      result.success ? null : `GetAllParameterNames: ${result.error ?? 'Unknown error'}`,
    );
    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetAllParameterNames gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetAllParameterNames: ${message}`);
    return { success: false, parameterNames: [], parameterCandidates: [], error: message };
  }
}
